"use client"

import { Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { ChevronDown } from "lucide-react"

import { SendableAddress } from "@/hooks/use-email-compose-logic"

export type { SendableAddress }

interface FromEmailSelectorProps {
  selectedAddress: SendableAddress | null
  allAddresses: SendableAddress[]
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (address: SendableAddress) => void
  label?: string
  className?: string
  onReset?: () => void
  resetLabel?: string
  disabled?: boolean
  emptyLabel?: string
}

export function FromEmailSelector({
  selectedAddress,
  allAddresses,
  isOpen,
  onOpenChange,
  onSelect,
  label = "From",
  className,
  disabled = false,
  emptyLabel = "Loading...",
  onReset,
  resetLabel = "Use my primary account",
}: FromEmailSelectorProps) {
  const hasMultipleAddresses =
    !disabled &&
    (!!onReset ||
      allAddresses.some(
        (address) => address.integrationId !== selectedAddress?.integrationId || address.email !== selectedAddress.email
      ))

  return (
    <div className={cn("flex flex-none items-center gap-x-2", className)}>
      <label className={cn("text-tertiary-foreground flex-none text-sm font-medium", label === "From" && "w-10")}>
        {label}
      </label>
      <div className="flex h-full grow flex-wrap items-center gap-1.5">
        {selectedAddress || onReset ? (
          <Popover open={isOpen} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild disabled={!hasMultipleAddresses}>
              <button
                type="button"
                className={cn(
                  "flex h-6 items-center rounded-md border pr-1 pl-1.5 text-sm/none",
                  hasMultipleAddresses && "hover:bg-secondary cursor-pointer"
                )}
              >
                <span>{selectedAddress?.email ?? resetLabel}</span>
                {hasMultipleAddresses && (
                  <ChevronDown className="text-icon hover:text-icon-active ml-0.5 h-3.5 w-3.5" />
                )}
              </button>
            </PopoverTrigger>
            {hasMultipleAddresses && (
              <PopoverContent align="start" className="w-auto min-w-[200px] p-1">
                <div className="flex flex-col">
                  {onReset && (
                    <button
                      type="button"
                      onClick={onReset}
                      className="hover:bg-secondary rounded-sm px-2 py-1.5 text-left text-sm"
                    >
                      {resetLabel}
                    </button>
                  )}
                  {allAddresses.map((address) => (
                    <button
                      key={`${address.integrationId}:${address.email}`}
                      type="button"
                      onClick={() => onSelect(address)}
                      className={cn(
                        "hover:bg-secondary flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                        address.email === selectedAddress?.email &&
                          address.integrationId === selectedAddress.integrationId &&
                          "bg-secondary"
                      )}
                    >
                      <span>{address.email}</span>
                      {allAddresses.some(
                        (other) => other.email === address.email && other.integrationId !== address.integrationId
                      ) && (
                        <span className="text-muted-foreground text-xs">
                          {address.integrationLabel ?? address.integrationId}
                        </span>
                      )}
                      {address.isPrimaryIntegration && <span className="text-muted-foreground text-xs">(Primary)</span>}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            )}
          </Popover>
        ) : (
          <span className="text-muted-foreground text-sm">{emptyLabel}</span>
        )}
      </div>
    </div>
  )
}
